import { c as _c } from "react/compiler-runtime";
// eslint-disable-next-line eag/no-external-date-time-format-functions -- Special format because of Datagrid
import { formatRFC3339 } from 'date-fns';
import { Chips, DateTimePicker, NumberInput, Separator, useTranslationContext } from 'platform/components';
import { HStack, VStack } from 'platform/foundation-tw';

// eslint-disable-next-line no-restricted-imports -- Intended
import { intersection, pathOr } from 'ramda';
import { isString } from 'ramda-adjunct';
import { suffixTestId } from 'shared/web';
import { quickFiltersConst } from '../../constants/quickFilters';
import { useFilterOnChipsChange } from '../../hooks/useFilterOnChipsChange';
import type { EagFilterProps } from '../../types/EagFilterProps';
import { parseDateFilter } from '../../utils/parseDateFilter';
type DateTimeRangeModel = {
  from: string | null;
  to: string | null;
};
type RelativeInputModel = {
  relativeInputValue: number;
  relativeInputKey: string;
};
export type DateTimeRangeFilterProps = EagFilterProps & {
  min: string | null;
  max: string | null;
  relativeDates: Array<{
    value: string;
    label: string;
  }>;
  relativeInputs: Array<{
    value: string;
    label: string;
  }>;
  quickFilters?: Array<{
    value: string;
    label: string;
  }>;
};
const FILTER_VALUE_KEY = 'value';
export function DateTimeRangeFilter(props) {
  const $ = _c(70);
  const t = useTranslationContext();
  let t0;
  if ($[0] !== props.min) {
    t0 = parseDateFilter(props.min);
    $[0] = props.min;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const minDate = t0;
  let t1;
  if ($[2] !== props.max) {
    t1 = parseDateFilter(props.max);
    $[2] = props.max;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const maxDate = t1;
  const model = props.value[FILTER_VALUE_KEY] as DateTimeRangeModel | RelativeInputModel | string | null;
  let t2;
  if ($[4] !== props) {
    t2 = newModel => {
      props.onChange({
        [FILTER_VALUE_KEY]: newModel
      });
    };
    $[4] = props;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const onModelChange = t2;
  let t3;
  if ($[6] !== onModelChange) {
    t3 = {
      setFilterValue: onModelChange,
      defaultValue: null
    };
    $[6] = onModelChange;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const {
    onChipsChange
  } = useFilterOnChipsChange(t3);
  let t4;
  if ($[8] !== model) {
    t4 = pathOr(null, ["from"], model);
    $[8] = model;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const fromPickerValue = t4;
  let t5;
  if ($[10] !== model) {
    t5 = pathOr(null, ["to"], model);
    $[10] = model;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const toPickerValue = t5;
  let t6;
  if ($[12] !== model) {
    t6 = pathOr(0, ["relativeInputValue"], model);
    $[12] = model;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const inputValue = t6;
  let t7;
  if ($[14] !== model) {
    t7 = isString(model) ? [model] : [];
    $[14] = model;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const chipsValue = t7;
  let t8;
  if ($[16] !== model || $[17] !== onModelChange) {
    t8 = value => {
      onModelChange({
        from: value ? formatRFC3339(value) : null,
        to: pathOr(null, ["to"], model)
      });
    };
    $[16] = model;
    $[17] = onModelChange;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const onDateFromChange = t8;
  let t9;
  if ($[19] !== model || $[20] !== onModelChange) {
    t9 = value_0 => {
      onModelChange({
        to: value_0 ? formatRFC3339(value_0) : null,
        from: pathOr(null, ["from"], model)
      });
    };
    $[19] = model;
    $[20] = onModelChange;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const onDateToChange = t9;
  let t10;
  if ($[22] !== onModelChange) {
    t10 = (relativeInputValue, relativeInputKey) => {
      const newModel_0 = isNaN(relativeInputValue) || relativeInputValue === 0 ? null : {
        relativeInputValue,
        relativeInputKey
      };
      onModelChange(newModel_0);
    };
    $[22] = onModelChange;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  const onInputChange = t10;
  let t11;
  if ($[24] !== model) {
    t11 = intersection([model], quickFiltersConst);
    $[24] = model;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const isQuickFilterValue = t11 as string[];
  let t12;
  if ($[26] !== isQuickFilterValue || $[27] !== onChipsChange || $[28] !== props) {
    t12 = props.quickFilters && props.quickFilters.length > 0 && <><HStack><Chips isDisabled={props.isDisabled} value={isQuickFilterValue} options={props.quickFilters} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId("date-time-range-filter-quickFilters", props)} isDeselectable={true} /></HStack><Separator /></>;
    $[26] = isQuickFilterValue;
    $[27] = onChipsChange;
    $[28] = props;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== chipsValue || $[31] !== onChipsChange || $[32] !== props) {
    t13 = props.relativeDates && props.relativeDates.length > 0 && <><Chips isDisabled={props.isDisabled} value={chipsValue} options={props.relativeDates} onChange={onChipsChange} isMultiple={false} data-testid={suffixTestId("date-time-range-filter-relativeDate", props)} isDeselectable={true} /><Separator /></>;
    $[30] = chipsValue;
    $[31] = onChipsChange;
    $[32] = props;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t) {
    t14 = t("page.datagrid.filter.dateTimeLabelFrom");
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const t15 = props.isDisabled;
  let t16;
  if ($[36] !== props) {
    t16 = suffixTestId("date-time-range-filter-from", props);
    $[36] = props;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== fromPickerValue || $[39] !== maxDate || $[40] !== minDate || $[41] !== onDateFromChange || $[42] !== props.isDisabled || $[43] !== t14 || $[44] !== t16) {
    t17 = <DateTimePicker label={t14} minDate={minDate} maxDate={maxDate} isDisabled={t15} value={fromPickerValue} onChange={onDateFromChange} data-testid={t16} isRelativeDatesHidden={true} />;
    $[38] = fromPickerValue;
    $[39] = maxDate;
    $[40] = minDate;
    $[41] = onDateFromChange;
    $[42] = props.isDisabled;
    $[43] = t14;
    $[44] = t16;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t) {
    t18 = t("page.datagrid.filter.dateTimeLabelTo");
    $[46] = t;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  const t19 = props.isDisabled;
  let t20;
  if ($[48] !== props) {
    t20 = suffixTestId("date-time-range-filter-to", props);
    $[48] = props;
    $[49] = t20;
  } else {
    t20 = $[49];
  }
  let t21;
  if ($[50] !== maxDate || $[51] !== minDate || $[52] !== onDateToChange || $[53] !== props.isDisabled || $[54] !== t18 || $[55] !== t20 || $[56] !== toPickerValue) {
    t21 = <DateTimePicker label={t18} minDate={minDate} maxDate={maxDate} isDisabled={t19} value={toPickerValue} onChange={onDateToChange} data-testid={t20} isRelativeDatesHidden={true} />;
    $[50] = maxDate;
    $[51] = minDate;
    $[52] = onDateToChange;
    $[53] = props.isDisabled;
    $[54] = t18;
    $[55] = t20;
    $[56] = toPickerValue;
    $[57] = t21;
  } else {
    t21 = $[57];
  }
  let t22;
  if ($[58] !== t17 || $[59] !== t21) {
    t22 = <VStack spacing={2}>{t17}{t21}</VStack>;
    $[58] = t17;
    $[59] = t21;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  let t23;
  if ($[61] !== inputValue || $[62] !== onInputChange || $[63] !== props) {
    t23 = props.relativeInputs.map(relativeInput => <NumberInput key={relativeInput.value} label={relativeInput.label} isDisabled={props.isDisabled} onChange={value_1 => onInputChange(value_1 ?? 0, relativeInput.value)} value={inputValue} data-testid={suffixTestId(`date-time-range-filter-relativeInput-${relativeInput.label}`, props)} />);
    $[61] = inputValue;
    $[62] = onInputChange;
    $[63] = props;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== t12 || $[66] !== t13 || $[67] !== t22 || $[68] !== t23) {
    t24 = <VStack>{t12}{t13}{t22}{t23}</VStack>;
    $[65] = t12;
    $[66] = t13;
    $[67] = t22;
    $[68] = t23;
    $[69] = t24;
  } else {
    t24 = $[69];
  }
  return t24;
}